import React, { useState, useEffect, useRef } from "react";
import { switchRequestAPI } from "../services/api";
import toast from "react-hot-toast";
import {
  SwapHoriz as SwapHorizIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Business as BusinessIcon,
  LocalLibrary as LocalLibraryIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  Bed as BedIcon,
  Chair as ChairIcon,
  History as HistoryIcon,
} from "@mui/icons-material";

const AdminSwitchRequests = () => {
  const [hostelAvailableRequests, setHostelAvailableRequests] = useState([]);
  const [hostelMutualRequests, setHostelMutualRequests] = useState([]);
  const [libraryAvailableRequests, setLibraryAvailableRequests] = useState([]);
  const [libraryMutualRequests, setLibraryMutualRequests] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // 'available' | 'mutual' | 'all'
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchInputRef = useRef(null);
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    requestId: null,
    scope: "hostel",
    requestType: "available", // "available" or "mutual"
    remarks: "",
    loading: false,
  });
  const [selectedMutualIds, setSelectedMutualIds] = useState([]);
  const [matching, setMatching] = useState(false);
  const [approving, setApproving] = useState({
    id: null,
    scope: null,
    type: null,
  });
  const [viewMode, setViewMode] = useState("requests"); // requests | history

  // State for history data
  const [hostelAvailableHistory, setHostelAvailableHistory] = useState([]);
  const [hostelMutualHistory, setHostelMutualHistory] = useState([]);
  const [libraryAvailableHistory, setLibraryAvailableHistory] = useState([]);
  const [libraryMutualHistory, setLibraryMutualHistory] = useState([]);

  // Reset selection when changing tabs or type
  useEffect(() => {
    setSelectedMutualIds([]);
  }, [activeTab, typeFilter]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data
  useEffect(() => {
    fetchAllData();
  }, [viewMode]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      if (viewMode === "requests") {
        const [
          hostelAvailableRes,
          hostelMutualRes,
          libraryAvailableRes,
          libraryMutualRes,
        ] = await Promise.all([
          switchRequestAPI.hostel.getAvailableSwitchRequests({
            status: "pending",
          }),
          switchRequestAPI.hostel.getMutualSwitchRequests({
            status: "pending",
          }),
          switchRequestAPI.library.getAvailableSwitchRequests({
            status: "pending",
          }),
          switchRequestAPI.library.getMutualSwitchRequests({
            status: "pending",
          }),
        ]);

        setHostelAvailableRequests(hostelAvailableRes.data || []);
        setHostelMutualRequests(hostelMutualRes.data || []);
        setLibraryAvailableRequests(libraryAvailableRes.data || []);
        setLibraryMutualRequests(libraryMutualRes.data || []);
      } else {
        const [
          hostelAvailableHistoryRes,
          hostelMutualHistoryRes,
          libraryAvailableHistoryRes,
          libraryMutualHistoryRes,
        ] = await Promise.all([
          switchRequestAPI.hostel.getAvailableSwitchHistory(),
          switchRequestAPI.hostel.getMutualSwitchHistory(),
          switchRequestAPI.library.getAvailableSwitchHistory(),
          switchRequestAPI.library.getMutualSwitchHistory(),
        ]);
        setHostelAvailableHistory(hostelAvailableHistoryRes.data || []);
        setHostelMutualHistory(hostelMutualHistoryRes.data || []);
        setLibraryAvailableHistory(libraryAvailableHistoryRes.data || []);
        setLibraryMutualHistory(libraryMutualHistoryRes.data || []);
      }
    } catch (error) {
      console.error(`Error fetching switch ${viewMode}:`, error);
      toast.error(`Failed to load switch ${viewMode}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openReject = (request, type, requestType) => {
    setRejectDialog({
      open: true,
      requestId: request.id,
      scope: type,
      requestType: requestType,
      remarks: "",
      loading: false,
    });
  };

  const submitReject = async () => {
    if (!rejectDialog.remarks.trim()) {
      toast.error("Please provide remarks");
      return;
    }
    setRejectDialog((s) => ({ ...s, loading: true }));
    try {
      const apiGroup =
        rejectDialog.scope === "hostel"
          ? switchRequestAPI.hostel
          : switchRequestAPI.library;

      const payload = {
        remarks: rejectDialog.remarks.trim(),
      };

      if (rejectDialog.requestType === "available") {
        await apiGroup.rejectAvailableSwitchRequest(
          rejectDialog.requestId,
          payload
        );
      } else {
        await apiGroup.rejectMutualSwitchRequest(
          rejectDialog.requestId,
          payload
        );
      }

      toast.success("Switch request rejected.");
      setRejectDialog({
        open: false,
        requestId: null,
        scope: "hostel",
        requestType: "available",
        remarks: "",
        loading: false,
      });
      await fetchAllData();
    } catch (error) {
      console.error("Reject failed:", error);
      toast.error(error.response?.data?.detail || "Failed to reject request");
      setRejectDialog((s) => ({ ...s, loading: false }));
    }
  };

  // Approve available request
  const handleApproveAvailable = async (requestId, scope) => {
    setApproving({ id: requestId, scope, type: "available" });
    try {
      const apiGroup =
        scope === "hostel" ? switchRequestAPI.hostel : switchRequestAPI.library;
      const res = await apiGroup.approveAvailableSwitchRequest(requestId);
      toast.success(
        res?.data?.detail || "Switch request approved successfully."
      );
      window.dispatchEvent(new CustomEvent("allocation-updated", { detail: { scope, type: "available", data: res?.data } }));
      await fetchAllData();
    } catch (error) {
      console.error("Approve failed:", error);
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to approve request");
    } finally {
      setApproving({ id: null, scope: null, type: null });
    }
  };

  // Manual match approval for mutual requests
  const handleToggleSelect = (id) => {
    setSelectedMutualIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev; // max two
      return [...prev, id];
    });
  };

  const handleManualMatchApprove = async () => {
    if (selectedMutualIds.length !== 2) {
      toast.error("Select exactly two mutual requests");
      return;
    }
    setMatching(true);
    try {
      const [a, b] = selectedMutualIds;
      const apiGroup =
        activeTab === 1 ? switchRequestAPI.library : switchRequestAPI.hostel;
      const payload = { request_a_id: a, request_b_id: b };
      const res = await apiGroup.matchAndApproveMutualSwitch(payload);
      const detail = res?.data?.detail || "Mutual switch approved.";
      toast.success(detail);
      setSelectedMutualIds([]);
      window.dispatchEvent(new CustomEvent("allocation-updated", { detail: { scope: activeTab === 1 ? "library" : "hostel", type: "mutual", data: res?.data } }));
      await fetchAllData();
    } catch (error) {
      console.error("Manual match failed:", error);
      toast.error(
        error.response?.data?.detail || error.response?.data?.error || "Failed to approve mutual switch"
      );
    } finally {
      setMatching(false);
    }
  };

  // Get current data based on active tab and type filter
  const getCurrentData = () => {
    let data = [];
    let type = "hostel";
    let source = viewMode === "requests" ? "requests" : "history";

    const dataMap = {
      requests: {
        hostel: {
          available: hostelAvailableRequests,
          mutual: hostelMutualRequests,
          all: [...hostelAvailableRequests, ...hostelMutualRequests],
        },
        library: {
          available: libraryAvailableRequests,
          mutual: libraryMutualRequests,
          all: [...libraryAvailableRequests, ...libraryMutualRequests],
        },
      },
      history: {
        hostel: {
          available: hostelAvailableHistory,
          mutual: hostelMutualHistory,
          all: [...hostelAvailableHistory, ...hostelMutualHistory],
        },
        library: {
          available: libraryAvailableHistory,
          mutual: libraryMutualHistory,
          all: [...libraryAvailableHistory, ...libraryMutualHistory],
        },
      },
    };

    switch (activeTab) {
      case 0: // Hostel
        type = "hostel";
        data = dataMap[source][type][typeFilter] || [];
        break;
      case 1: // Library
        type = "library";
        data = dataMap[source][type][typeFilter] || [];
        break;
      default:
        data = [];
        type = "hostel";
    }

    return { data, type };
  };

  // Filter data based on search and filters
  const getFilteredData = (data) => {
    return data.filter((item) => {
      const isHistory = viewMode === "history";
      const search = debouncedSearch.toLowerCase();
      if (isHistory) {
        if (typeFilter === "mutual") {
          return (
            item.student_a?.toLowerCase().includes(search) ||
            item.student_b?.toLowerCase().includes(search)
          );
        } else {
          return item.student?.toLowerCase().includes(search);
        }
      }

      const matchesSearch =
        debouncedSearch === "" ||
        item.student?.name
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.current_room
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.current_bed
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.current_seat
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.target_room
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.target_bed_number
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        item.target_seat_number
          ?.toLowerCase()
          .includes(debouncedSearch.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  const { data: currentData, type: currentType } = getCurrentData();
  const filteredData = getFilteredData(currentData);

  const handleClearSearch = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircleIcon className="text-green-600" />;
      case "rejected":
        return <CancelIcon className="text-red-600" />;
      case "pending":
        return <PendingIcon className="text-yellow-600" />;
      default:
        return <PendingIcon className="text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isHistory = viewMode === "history";

  // Switch Request Card Component for Mobile
  const SwitchRequestCard = ({ request, type, requestType }) => {
    const isApproving =
      approving.id === request.id &&
      approving.scope === type &&
      approving.type === requestType;

    return (
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-all duration-200">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {type === "hostel" ? (
                <BusinessIcon className="text-purple-600" />
              ) : (
                <LocalLibraryIcon className="text-blue-600" />
              )}
              <span className="text-sm font-medium text-gray-500 uppercase">
                {type === "hostel" ? "Hostel" : "Library"} -{" "}
                {requestType === "available" ? "Available" : "Mutual"}
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                isHistory ? request.action : request.status
              )}`}
            >
              {getStatusIcon(isHistory ? request.action : request.status)}
              <span className="capitalize">
                {isHistory ? request.action : request.status}
              </span>
            </span>
          </div>

          {/* Student Info */}
          <div className="flex items-center gap-2">
            <PersonIcon className="text-gray-400" />
            <div>
              <h3 className="font-semibold text-gray-900">
                {isHistory
                  ? request.student ||
                    `${request.student_a} & ${request.student_b}`
                  : request.student?.name || "Unknown"}
              </h3>
              <p className="text-sm text-gray-500">
                ID: {request.student?.id || request.booking}
              </p>
            </div>
          </div>

          {/* Current Location */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <LocationOnIcon className="text-gray-400 text-sm" />
              <span className="text-sm font-medium text-gray-700">
                {isHistory ? "Previous Location" : "Current Location"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {type === "hostel" ? (
                <>
                  <BedIcon className="text-gray-400 text-sm" />
                  <span className="text-sm text-gray-600">
                    {isHistory
                      ? `${request.previous_room} - Bed ${request.previous_bed}`
                      : `${request.current_room} - Bed ${request.current_bed}`}
                  </span>
                </>
              ) : (
                <>
                  <ChairIcon className="text-gray-400 text-sm" />
                  <span className="text-sm text-gray-600">
                    Seat{" "}
                    {isHistory ? request.previous_seat : request.current_seat}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Requested Location (for available requests) */}
          {(requestType === "available" || isHistory) &&
            (request.target_room ||
              request.target_seat_number ||
              request.new_room ||
              request.new_seat) && (
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <LocationOnIcon className="text-blue-400 text-sm" />
                  <span className="text-sm font-medium text-blue-700">
                    {isHistory ? "New Location" : "Requested Location"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {type === "hostel" ? (
                    <>
                      <BedIcon className="text-blue-400 text-sm" />
                      <span className="text-sm text-blue-600">
                        {isHistory
                          ? `${request.new_room} - Bed ${request.new_bed}`
                          : `${request.target_room} - Bed ${request.target_bed_number}`}
                      </span>
                    </>
                  ) : (
                    <>
                      <ChairIcon className="text-blue-400 text-sm" />
                      <span className="text-sm text-blue-600">
                        Seat{" "}
                        {isHistory
                          ? request.new_seat
                          : request.target_seat_number}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

          {/* Mutual Switch Info */}
          {requestType === "mutual" && !isHistory && (
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <SwapHorizIcon className="text-purple-400 text-sm" />
                <span className="text-sm font-medium text-purple-700">
                  Mutual Switch
                </span>
              </div>
              <div className="text-sm text-purple-600">
                {request.partner_student
                  ? `With: ${request.partner_student.name}`
                  : "No partner yet"}
              </div>
            </div>
          )}

          {/* Remarks */}
          {request.remarks && (
            <div className="bg-yellow-50 rounded-lg p-3">
              <div className="text-sm text-yellow-700">
                <strong>Remarks:</strong> {request.remarks}
              </div>
            </div>
          )}

          {/* Date */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <EventIcon className="text-gray-400" />
            <span>
              {isHistory ? "Changed:" : "Requested:"}{" "}
              {formatDate(isHistory ? request.changed_on : request.created_at)}
            </span>
          </div>

          {/* Actions */}
          {!isHistory && request.status === "pending" && (
            <div className="pt-2 grid grid-cols-2 gap-2">
              {requestType === "available" && (
                <button
                  onClick={() => handleApproveAvailable(request.id, type)}
                  disabled={isApproving}
                  className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-white ${
                    isApproving
                      ? "bg-green-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isApproving ? "Approving..." : "Approve"}
                </button>
              )}
              <button
                onClick={() => openReject(request, type, requestType)}
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700"
              >
                <CancelIcon fontSize="small" /> Reject
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { label: "Hostel", value: 0 },
    { label: "Library", value: 1 },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <SwapHorizIcon className="text-purple-600 text-2xl" />
              <h1 className="text-2xl font-bold text-gray-800">
                Switch Management
              </h1>
            </div>
            <div className="flex items-center gap-2 bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode("requests")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  viewMode === "requests"
                    ? "bg-white text-purple-600 shadow"
                    : "text-gray-600 hover:bg-gray-300"
                }`}
              >
                Pending Requests
              </button>
              <button
                onClick={() => setViewMode("history")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${
                  viewMode === "history"
                    ? "bg-white text-purple-600 shadow"
                    : "text-gray-600 hover:bg-gray-300"
                }`}
              >
                <HistoryIcon fontSize="small" />
                History
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 border-b border-gray-200">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.value
                      ? "border-purple-500 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Secondary tabs for Available / Mutual */}
          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-3">
                <button
                  onClick={() => setTypeFilter("all")}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    typeFilter === "all"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setTypeFilter("available")}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    typeFilter === "available"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Available
                </button>
                <button
                  onClick={() => setTypeFilter("mutual")}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    typeFilter === "mutual"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Mutual
                </button>
              </div>

              {/* Manual match approve control */}
              {!isHistory && typeFilter === "mutual" && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Selected: {selectedMutualIds.length}/2
                  </span>
                  <button
                    onClick={handleManualMatchApprove}
                    disabled={selectedMutualIds.length !== 2 || matching}
                    className={`px-4 py-2 rounded-lg text-white ${
                      selectedMutualIds.length === 2 && !matching
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-green-400 cursor-not-allowed"
                    }`}
                  >
                    {matching ? "Approving..." : "Match & Approve"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={
                    isHistory
                      ? "Search by student name..."
                      : "Search by student name, room, bed, seat..."
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Clear Button */}
            {(searchQuery ||
              statusFilter !== "all" ||
              typeFilter !== "all") && (
              <button
                onClick={handleClearSearch}
                className="flex items-center gap-2 px-4 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors duration-200"
              >
                <ClearIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Clear</span>
              </button>
            )}
          </div>

          {/* Search Results Info */}
          {(searchQuery || statusFilter !== "all" || typeFilter !== "all") && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-purple-800">
                <SearchIcon className="w-4 h-4" />
                <span>
                  {searchQuery && `Searching for: "${searchQuery}"`}
                  {searchQuery && statusFilter !== "all" && " and "}
                  {statusFilter !== "all" && `Status: ${statusFilter}`}
                  {!searchQuery &&
                    statusFilter !== "all" &&
                    `Filtering by status: ${statusFilter}`}
                </span>
                <span className="text-purple-600 font-medium">
                  • {filteredData.length} result
                  {filteredData.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          {filteredData.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <SwapHorizIcon className="text-gray-400 text-4xl mx-auto mb-4" />
              <p className="text-gray-500 font-medium">
                No {isHistory ? "history records" : "requests"} found
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {searchQuery || statusFilter !== "all" || typeFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "No data available"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredData.map((item) => (
                <div key={item.id}>
                  <SwitchRequestCard
                    request={item}
                    type={currentType}
                    requestType={
                      isHistory
                        ? item.student
                          ? "available"
                          : "mutual"
                        : item.target_bed || item.target_seat
                        ? "available"
                        : "mutual"
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                <tr>
                  {!isHistory && typeFilter === "mutual" && (
                    <th className="px-4 py-4 text-left text-sm font-semibold">
                      Select
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    {isHistory && typeFilter === "mutual"
                      ? "Students"
                      : "Student"}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    {isHistory ? "Previous Location" : "Current Location"}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    {isHistory ? "New Location" : "Requested Location"}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    {isHistory ? "Action" : "Status"}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    {isHistory ? "Changed On" : "Date"}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Remark
                  </th>
                  {!isHistory && (
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={!isHistory && typeFilter === "mutual" ? 8 : 7}
                      className="px-6 py-8 text-center"
                    >
                      <SwapHorizIcon className="text-gray-400 text-4xl mx-auto mb-4" />
                      <p className="text-gray-500 font-medium">
                        No {isHistory ? "history" : "requests"} found
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        {searchQuery ||
                        statusFilter !== "all" ||
                        typeFilter !== "all"
                          ? "Try adjusting your search or filters"
                          : "No data available"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => {
                    const isSelectable =
                      !isHistory &&
                      typeFilter === "mutual" &&
                      !item.target_bed &&
                      !item.target_seat &&
                      item.status === "pending";
                    const isChecked = selectedMutualIds.includes(item.id);
                    const reachedLimit =
                      selectedMutualIds.length >= 2 && !isChecked;
                    const isAvailable =
                      !isHistory && (!!item.target_bed || !!item.target_seat);
                    const isMutual = !isHistory && !isAvailable;
                    const isApproving =
                      !isHistory &&
                      approving.id === item.id &&
                      approving.scope === currentType &&
                      approving.type === (isAvailable ? "available" : "mutual");

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        {!isHistory && typeFilter === "mutual" && (
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleSelect(item.id)}
                              disabled={!isSelectable || reachedLimit}
                              className="w-4 h-4 accent-purple-600 cursor-pointer disabled:cursor-not-allowed"
                            />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">
                              {isHistory
                                ? item.student ||
                                  `${item.student_a} & ${item.student_b}`
                                : item.student?.name || "Unknown"}
                            </div>
                            {!isHistory && (
                              <div className="text-sm text-gray-500">
                                ID: {item.student?.id || item.booking}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {currentType === "hostel" ? (
                              <BusinessIcon className="text-purple-600" />
                            ) : (
                              <LocalLibraryIcon className="text-blue-600" />
                            )}
                            <span className="text-sm font-medium text-gray-600 capitalize">
                              {currentType} -{" "}
                              {isHistory
                                ? item.student
                                  ? "Available"
                                  : "Mutual"
                                : isAvailable
                                ? "Available"
                                : "Mutual"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {currentType === "hostel" ? (
                              <>
                                <BedIcon className="text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {isHistory
                                    ? `${item.previous_room} - Bed ${item.previous_bed}`
                                    : `${item.current_room} - Bed ${item.current_bed}`}
                                </span>
                              </>
                            ) : (
                              <>
                                <ChairIcon className="text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  Seat{" "}
                                  {isHistory
                                    ? item.previous_seat
                                    : item.current_seat}
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {currentType === "hostel" ? (
                              <>
                                <BedIcon className="text-blue-400" />
                                <span className="text-sm text-blue-600">
                                  {isHistory
                                    ? `${item.new_room} - Bed ${item.new_bed}`
                                    : `${item.target_room} - Bed ${item.target_bed_number}`}
                                </span>
                              </>
                            ) : (
                              <>
                                <ChairIcon className="text-blue-400" />
                                <span className="text-sm text-blue-600">
                                  Seat{" "}
                                  {isHistory
                                    ? item.new_seat
                                    : item.target_seat_number}
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                              isHistory ? item.action : item.status
                            )}`}
                          >
                            {getStatusIcon(
                              isHistory ? item.action : item.status
                            )}
                            <span className="capitalize">
                              {isHistory ? item.action : item.status}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(
                            isHistory ? item.changed_on : item.created_at
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.remarks || "-"}
                        </td>
                        {!isHistory && (
                          <td className="px-6 py-4">
                            {item.status === "pending" ? (
                              <div className="flex gap-2">
                                {isAvailable && (
                                  <button
                                    onClick={() =>
                                      handleApproveAvailable(
                                        item.id,
                                        currentType
                                      )
                                    }
                                    disabled={isApproving}
                                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${
                                      isApproving
                                        ? "bg-green-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700"
                                    }`}
                                  >
                                    {isApproving ? "Approving..." : "Approve"}
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    openReject(
                                      item,
                                      currentType,
                                      isAvailable ? "available" : "mutual"
                                    )
                                  }
                                  className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700"
                                >
                                  <CancelIcon fontSize="small" /> Reject
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reject Remarks Modal */}
        {rejectDialog.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-40"
              onClick={() =>
                !rejectDialog.loading &&
                setRejectDialog({
                  open: false,
                  requestId: null,
                  scope: "hostel",
                  requestType: "available",
                  remarks: "",
                  loading: false,
                })
              }
            />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Reject{" "}
                {rejectDialog.requestType === "available"
                  ? "Available"
                  : "Mutual"}{" "}
                Switch Request
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Provide a reason for rejection. This will be stored as remarks.
              </p>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={4}
                placeholder="e.g., Bed already taken"
                value={rejectDialog.remarks}
                onChange={(e) =>
                  setRejectDialog((s) => ({ ...s, remarks: e.target.value }))
                }
                disabled={rejectDialog.loading}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() =>
                    setRejectDialog({
                      open: false,
                      requestId: null,
                      scope: "hostel",
                      requestType: "available",
                      remarks: "",
                      loading: false,
                    })
                  }
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={rejectDialog.loading}
                >
                  Cancel
                </button>
                <button
                  onClick={submitReject}
                  className={`px-4 py-2 rounded-lg text-white ${
                    rejectDialog.loading
                      ? "bg-red-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                  disabled={rejectDialog.loading}
                >
                  {rejectDialog.loading ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSwitchRequests;
